import type { IsoDateTime } from './common';
import type { AllocationId, OrderId, OrderItemId, PackingStationId, ProductId, UserId } from './ids';
import type { MarketplaceChannel } from './marketplace';
import type { ChannelSyncWarning } from './operations';
import type { CancelledLine } from './orders';
import type { PickTote } from './picking';
import type { CarrierCode, PrintJob, ShippingModel } from './shipping';
import type { UrgentAction } from './urgent';

export const PACKING_ERROR_CODES = [
  'ORDER_NOT_FOUND',
  'ORDER_NOT_PACKABLE',
  'UNKNOWN_BARCODE',
  'WRONG_PRODUCT',
  'QUANTITY_EXCEEDED',
  'LABEL_NOT_READY',
  'WAYBILL_NOT_READY',
  'CONCURRENT_UPDATE',
  'SCAN_REQUIRED',
] as const;
export type PackingErrorCode = (typeof PACKING_ERROR_CODES)[number];

/** Body of POST /api/wms/verify-and-pack: one barcode scan at the packing station. */
export interface VerifyAndPackRequest {
  readonly stationId: PackingStationId;
  readonly orderId: OrderId;
  /** Raw scanner input; normalised server-side (trim, whitespace removed, ı/İ → I, upper-case) */
  readonly barcode: string;
  /** Scans at once (default 1); multiplied by the barcode's unitsPerScan (an ITF-14 case may count 24) */
  readonly quantity?: number;
  /** Carton chosen for the parcel (carton_types.code); its desi is billed. Default: estimated from the items */
  readonly cartonCode?: string;
  /** confirmed by sight, not read from the goods: products that carry no label (unitLabels false), or any by a supervisor */
  readonly byEye?: boolean;
  /** set by the API from the caller's role: a supervisor may confirm labelled goods by sight too */
  readonly supervisor?: boolean;
}

/**
 * "Eksik / hasarlı" at the desk: why units of an open line cannot go into the parcel. missing: not in
 * the tote; damaged: broken, booked to the damaged area; wrong_product: another product came instead.
 */
export const PACKING_SHORTAGE_REASONS = ['missing', 'damaged', 'wrong_product'] as const;
export type PackingShortageReason = (typeof PACKING_SHORTAGE_REASONS)[number];

export const PACKING_SHORTAGE_LABELS: Readonly<Record<PackingShortageReason, string>> = {
  missing: 'Sepette yok',
  damaged: 'Hasarlı',
  wrong_product: 'Yanlış ürün geldi',
};

/** Body of POST /api/wms/packing/:id/shortage. */
export interface PackingShortageRequest {
  readonly stationId: PackingStationId;
  readonly orderItemId: OrderItemId;
  /** Units of the line, at most the ones at the desk not scanned yet */
  readonly qty: number;
  readonly reason: PackingShortageReason;
  /** The line's missing units the screen showed: a report that finds more was sent twice and is refused */
  readonly expectedMissing?: number;
}

export interface PackingShortageResult {
  /** The order, now on hold in the exceptions queue */
  readonly verification: PackingVerification;
  /** Where a broken unit goes: its stock was booked there */
  readonly damagedArea: string | null;
  /** Bins locked for a count (göz sayımı) */
  readonly countBins: readonly string[];
}

export const PACKING_SESSION_STATUSES = ['in_progress', 'completed', 'aborted'] as const;
export type PackingSessionStatus = (typeof PACKING_SESSION_STATUSES)[number];

export interface PackingLine {
  readonly orderItemId: OrderItemId;
  readonly productId: ProductId;
  readonly sku: string;
  readonly name: string;
  readonly imageUrl: string | null;
  readonly required: number;
  readonly packed: number;
  /** false: the goods carry no label, the packer confirms them by sight */
  readonly unitLabels: boolean;
  /** the product leaves in its own box: an order of one such unit skips the packing table */
  readonly shipsAsIs: boolean;
  /** the product's barcode (else its SKU): what a confirmation by sight stands for */
  readonly code: string;
  /** Units reported missing or broken at the desk, no longer held for the order: picked again after the supervisor's retry */
  readonly missing: number;
}

export interface PackingScan {
  readonly barcode: string;
  readonly orderItemId: OrderItemId;
  /** Units the scan counted */
  readonly units: number;
}

/** orders.parcel_count's bounds: a label prints one page per box */
export const MAX_PARCELS = 99;

/**
 * The boxes an order needs at least: each unit that leaves in its own box (ships as is) is one,
 * the other units share one. A count below this means a box without a label.
 */
export function expectedParcels(lines: readonly { readonly required: number; readonly shipsAsIs: boolean }[]): number {
  const own = lines.reduce((n, l) => n + (l.shipsAsIs ? l.required : 0), 0);
  const boxed = lines.some((l) => !l.shipsAsIs && l.required > 0);
  return Math.min(MAX_PARCELS, Math.max(1, own + (boxed ? 1 : 0)));
}

/** Packing progress of one order, returned after every scan. */
export interface PackingVerification {
  readonly orderId: OrderId;
  readonly orderNumber: string;
  readonly tenantCode: string;
  /** Which pile at Sevkiyat the parcel goes to: its carrier and whose contract it ships under */
  readonly channel: MarketplaceChannel;
  readonly shippingModel: ShippingModel;
  readonly carrierCode: CarrierCode | null;
  /** Boxes the order leaves in: its label prints one page per box ("Koli 1/2", …) */
  readonly parcels: number;
  /** The label came from the marketplace or a carrier's system: its box count is theirs, not the desk's */
  readonly parcelsFixed: boolean;
  /** Its label was printed when the pick route was made and went on the goods at the bin: not printed again */
  readonly labelTakenAlong: boolean;
  readonly status: 'in_progress' | 'completed';
  readonly lines: readonly PackingLine[];
  readonly lastScan: PackingScan | null;
  /** Set by the scan that completed the order */
  readonly completion: PackingCompletion | null;
  /** The cart tote its picked units came in (Rota · Sepet), for the desk's header */
  readonly pickTote?: PickTote | null;
  /** Set while the order waits in the exceptions queue after a desk reported units missing or broken */
  readonly holdReason: string | null;
  /** Back from the exceptions queue with its missing units picked: its parcel waits on the BEKLEYEN shelf until the desk scans again */
  readonly shelved: boolean;
  /**
   * Lines the marketplace cancelled once the order was on the floor (migration 0062): not in `lines`, not
   * to go into the parcel; units of them that were picked wait in Yerleştirme → Rafa iade. Present when any.
   */
  readonly cancelledLines?: readonly CancelledLine[];
  /** The parcel was closed when a line was cancelled: opened again, it is packed without it. Present when true. */
  readonly repack?: true;
  /** The order's marketplace account is not read (keys refused, closed, stale): a cancel there is not seen. Present when so. */
  readonly syncWarning?: ChannelSyncWarning;
  /**
   * KARGO DEĞİŞTİ: the marketplace moved the parcel to another carrier after its label was printed and the
   * acil iş is open (migration 0069). The old label comes off; packing here prints the new one (a packed
   * parcel's new label is scanned at Sevkiyat or on the acil iş panel). Present when so.
   */
  readonly relabel?: Pick<UrgentAction, 'id' | 'title' | 'instruction' | 'details'>;
}

export interface PackingCompletion {
  readonly packedAt: IsoDateTime;
  readonly packedBy: UserId;
  /** Marketplace/carrier label and the seller's e-İrsaliye, sent to the station's thermal printer */
  readonly printJobs: readonly PrintJob[];
  /** Allocations consumed: physical and reserved stock both drop by the packed quantity */
  readonly consumedAllocationIds: readonly AllocationId[];
  /** Channel status push (e.g. Trendyol Picking → Invoiced), queued on BullMQ */
  readonly channelUpdate: 'queued' | 'not_required';
}

export interface PackingError {
  readonly code: PackingErrorCode;
  /** Turkish, shown to the operator as-is */
  readonly message: string;
  /** What the order still expects, to show next to the error */
  readonly expected?: readonly Pick<PackingLine, 'sku' | 'name' | 'required' | 'packed'>[];
  readonly scanned?: { readonly barcode: string; readonly sku: string | null; readonly name: string | null };
}

export type VerifyAndPackResponse =
  | { readonly ok: true; readonly verification: PackingVerification }
  | { readonly ok: false; readonly error: PackingError; readonly verification: PackingVerification | null };
